import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Eye, Check, X, Clock, User } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button } from "../../components/common/Primitives";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function AdminDocChangeRequests() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [bulkIds, setBulkIds] = useState(new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/doc-change-requests?status=${statusFilter}`);
      setItems(data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const openPreview = async (req) => {
    setSelected(req);
    setPreviewUrl(null);
    try {
      const resp = await api.get(`/admin/doc-change-requests/${req.request_id}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = (req) => setApproveFor(req);
  const reject = (req) => setRejectFor(req);

  const confirmApprove = async ({ reason }) => {
    const req = approveFor;
    if (!req) return;
    setBusy(true);
    try {
      await api.post(`/admin/doc-change-requests/${req.request_id}/approve`, { reason });
      toast.success("Change approved. Requester notified.");
      setApproveFor(null);
      if (selected?.request_id === req.request_id) { setSelected(null); setPreviewUrl(null); }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const confirmReject = async ({ reason }) => {
    const req = rejectFor;
    if (!req) return;
    setBusy(true);
    try {
      await api.post(`/admin/doc-change-requests/${req.request_id}/reject`, { reason });
      toast.success("Change rejected. Requester notified.");
      setRejectFor(null);
      if (selected?.request_id === req.request_id) { setSelected(null); setPreviewUrl(null); }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleBulk = (id) => {
    setBulkIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllPending = () => {
    const ids = new Set(items.filter((i) => i.status === "pending").map((i) => i.request_id));
    setBulkIds(ids);
  };
  const clearBulk = () => setBulkIds(new Set());
  const confirmBulkApprove = async ({ reason }) => {
    if (bulkIds.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/doc-change-requests/bulk-approve`, {
        request_ids: Array.from(bulkIds),
        reason: reason || "",
      });
      toast.success(`Approved ${data.approved?.length || 0} of ${bulkIds.size}. Skipped ${data.skipped?.length || 0}.`);
      setBulkApproveOpen(false);
      clearBulk();
      if (selected?.request_id && bulkIds.has(selected.request_id)) { setSelected(null); setPreviewUrl(null); }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const stats = useMemo(() => ({
    pending: items.filter((r) => r.status === "pending").length,
    approved: items.filter((r) => r.status === "approved").length,
    rejected: items.filter((r) => r.status === "rejected").length,
  }), [items]);

  return (
    <DashboardShell
      title="Doc Change Requests"
      subtitle="Diligence documents replaced 3+ times need an admin's blessing before going live. Preview, approve or reject each change here."
    >
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); clearBulk(); }}
            className={`px-3 py-1.5 rounded font-heading text-xs uppercase tracking-widest border ${
              statusFilter === s
                ? "bg-gold/10 text-[#8A6600] border-gold"
                : "text-ink-muted border-navy/15 hover:border-gold/50"
            }`}
            data-testid={`dcr-filter-${s}`}
          >
            {s} · {statusFilter === s ? items.length : stats[s] || 0}
          </button>
        ))}
        {statusFilter === "pending" && items.length > 0 && (
          <>
            <button
              onClick={selectAllPending}
              className="px-3 py-1.5 rounded font-heading text-xs uppercase tracking-widest border border-navy/15 text-ink-muted hover:border-gold/50 hover:text-[#8A6600]"
              data-testid="dcr-select-all"
            >
              Select all
            </button>
            {bulkIds.size > 0 && (
              <div className="ml-auto flex items-center gap-2" data-testid="dcr-bulk-toolbar">
                <span className="text-xs text-ink-muted">{bulkIds.size} selected</span>
                <Button onClick={() => setBulkApproveOpen(true)} data-testid="dcr-bulk-approve-btn">
                  <Check size={12} className="inline mr-1" /> Approve {bulkIds.size} together
                </Button>
                <button
                  onClick={clearBulk}
                  className="text-[11px] font-mono uppercase text-ink-muted hover:text-ink"
                  data-testid="dcr-bulk-clear"
                >Clear</button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5">
          <Card testId="dcr-list-card">
            <div className="overline mb-3">Requests</div>
            {items.length === 0 && (
              <div className="text-sm text-ink-muted italic">Nothing here.</div>
            )}
            <div className="space-y-2">
              {items.map((r) => (
                <div
                  key={r.request_id}
                  className={`w-full rounded border p-3 transition-colors flex items-start gap-2 ${
                    selected?.request_id === r.request_id
                      ? "border-gold bg-gold/[0.04]"
                      : "border-navy/10 hover:border-gold/40 bg-white/40"
                  }`}
                  data-testid={`dcr-item-${r.request_id}`}
                >
                  {r.status === "pending" && (
                    <input
                      type="checkbox"
                      checked={bulkIds.has(r.request_id)}
                      onChange={() => toggleBulk(r.request_id)}
                      className="mt-1 accent-[#02BB8E]"
                      data-testid={`dcr-check-${r.request_id}`}
                      aria-label={`Select ${r.filename}`}
                    />
                  )}
                  <button
                    onClick={() => openPreview(r)}
                    className="text-left flex-1 min-w-0"
                    data-testid={`dcr-open-${r.request_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 truncate">
                        <div className="text-ink font-heading text-sm truncate">{r.filename}</div>
                        <div className="text-[11px] font-mono text-ink-muted mt-0.5 flex items-center gap-2">
                          <User size={10} /> {r.requested_by_email}
                          <Clock size={10} /> {relTime(r.created_at)}
                        </div>
                      </div>
                      {r.status === "pending" ? <Pill tone="warn">Pending</Pill>
                        : r.status === "approved" ? <Pill tone="success">Approved</Pill>
                        : <Pill tone="danger">Rejected</Pill>}
                    </div>
                    {r.reason && (
                      <div className="text-xs text-ink-soft mt-2 italic line-clamp-2">&ldquo;{r.reason}&rdquo;</div>
                    )}
                    <div className="text-[10px] font-mono text-ink-faint mt-1">
                      Change #{(r.prior_change_count || 0) + 1} · {(r.size / 1024).toFixed(0)} KB
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-7">
          <Card testId="dcr-preview-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="overline flex items-center gap-1.5"><Eye size={12} className="text-gold" /> Preview</div>
              {selected && selected.status === "pending" && (
                <div className="flex items-center gap-2">
                  <Button variant="dark" onClick={() => reject(selected)} className="!bg-rose-700 hover:!bg-rose-800" data-testid="dcr-reject-btn">
                    <X size={12} className="inline mr-1" /> Reject
                  </Button>
                  <Button onClick={() => approve(selected)} data-testid="dcr-approve-btn">
                    <Check size={12} className="inline mr-1" /> Approve & Publish
                  </Button>
                </div>
              )}
            </div>
            {!selected && (
              <div className="text-sm text-ink-muted italic text-center py-16">
                <FileText size={22} className="text-ink-muted mx-auto mb-2 opacity-50" />
                Select a request on the left to preview the proposed file.
              </div>
            )}
            {selected && (
              <div className="space-y-3">
                <div className="text-xs text-ink-soft grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Deal Room</div><div className="text-ink font-mono">{selected.deal_room_id}</div></div>
                  <div><div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Doc</div><div className="text-ink font-mono">{selected.doc_id}</div></div>
                  <div><div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Requester</div><div className="text-ink">{selected.requested_by_email}</div></div>
                  <div><div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Filed</div><div className="text-ink">{relTime(selected.created_at)}</div></div>
                </div>
                {selected.reason && (
                  <div className="rounded border border-gold/30 bg-gold/[0.04] p-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600]">Requester&apos;s reason</div>
                    <div className="text-sm text-ink italic mt-1">&ldquo;{selected.reason}&rdquo;</div>
                  </div>
                )}
                <div className="rounded border border-navy/10 overflow-hidden" style={{ height: "70vh" }}>
                  {previewUrl ? (
                    <iframe src={previewUrl} title="Change preview" className="w-full h-full" data-testid="dcr-preview-iframe" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-ink-muted text-sm">Loading preview…</div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <ConfirmReasonDialog
        open={!!approveFor}
        title={`Approve change to ${approveFor?.filename || "this document"}?`}
        description="The proposed file will replace the live version in the deal room. Both parties will be notified. Prior versions remain in the change history."
        confirmLabel="Approve & Publish"
        tone="emerald"
        reasonRequired={false}
        reasonLabel="Optional note for the audit log"
        reasonPlaceholder=""
        onConfirm={confirmApprove}
        onClose={() => setApproveFor(null)}
        testId="dcr-approve-dialog"
      />
      <ConfirmReasonDialog
        open={!!rejectFor}
        title={`Reject change to ${rejectFor?.filename || "this document"}?`}
        description="The proposed file will be discarded and the live version stays put. The requester will see your reason in their notification."
        confirmLabel="Reject Change"
        tone="danger"
        reasonPlaceholder="e.g. Two prior versions are already fine — please explain what changed."
        onConfirm={confirmReject}
        onClose={() => setRejectFor(null)}
        testId="dcr-reject-dialog"
      />
      <ConfirmReasonDialog
        open={bulkApproveOpen}
        title={`Approve ${bulkIds.size} change request${bulkIds.size === 1 ? "" : "s"} in one go?`}
        description={`Every selected proposed file will replace its live version. Requesters and counterparties are notified individually. Skipped items (already resolved, missing file) are reported in the toast.`}
        confirmLabel={`Approve ${bulkIds.size} together`}
        tone="emerald"
        reasonRequired={false}
        reasonLabel="Batch note for the audit log (optional)"
        reasonPlaceholder="e.g. Founder resubmitted the diligence pack after our review call."
        onConfirm={confirmBulkApprove}
        onClose={() => setBulkApproveOpen(false)}
        testId="dcr-bulk-approve-dialog"
      />
    </DashboardShell>
  );
}
